import React, { useEffect, useState } from 'react';
import { ethers } from 'ethers';
import { useContracts } from '../../hooks/useContract';
import { invalidatePackageTiersCache } from '../../hooks/usePackageTiers';
import { useNotify } from '../../context/NotificationContext';
import { parseUSDT } from '../../utils/format';
import { parseError } from '../../utils/ethers';
import { TIER_LABELS } from '../../utils/constants';
import { Spinner } from '../common/Loader';

export default function TiersPanel() {
  const { read, write } = useContracts();
  const notify = useNotify();
  const [tiers, setTiers] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [busy, setBusy] = useState(-1);

  const load = () => {
    read.storage.getPackageTiers().then((data) => {
      const parsed = data.map((t) => ({
        priceUSDT: ethers.utils.formatUnits(t.priceUSDT, 18),
        miningDays: t.miningDays.toString(),
        dailyRewardUSDT: ethers.utils.formatUnits(t.dailyRewardUSDT, 18),
        active: t.active,
      }));
      setTiers(parsed);
      setDrafts(parsed);
    }).catch(() => {});
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const update = (idx, field, value) => {
    const copy = drafts.slice();
    copy[idx] = { ...copy[idx], [field]: value };
    setDrafts(copy);
  };

  async function save(idx) {
    if (!write) return notify.error('Wallet not connected.');
    const d = drafts[idx];
    setBusy(idx);
    try {
      const tx = await write.storage.setPackageTier(
        idx,
        parseUSDT(d.priceUSDT),
        d.miningDays || 0,
        parseUSDT(d.dailyRewardUSDT),
        d.active
      );
      notify.info('Saving tier...');
      await tx.wait();
      // Invalidate shared cache so users see the update on next mount
      invalidatePackageTiersCache();
      notify.success(`Tier ${idx + 1} updated`);
      load();
    } catch (err) {
      notify.error(parseError(err));
    } finally {
      setBusy(-1);
    }
  }

  if (!drafts.length) return <div className="admin-panel"><Spinner /></div>;

  return (
    <div className="admin-panel">
      <h3>Package Tiers</h3>
      <p className="panel-hint">Configure the 9 tiers. Inactive tiers cannot be purchased.</p>

      {drafts.map((d, i) => {
        const changed = JSON.stringify(d) !== JSON.stringify(tiers[i]);
        return (
          <div key={i} className="tier-editor">
            <div className="tier-editor-header">
              <div className="name">Tier {i + 1} · {TIER_LABELS[i]}</div>
              <button
                className={`tier-toggle ${d.active ? 'active' : ''}`}
                onClick={() => update(i, 'active', !d.active)}
              >
                {d.active ? '● Active' : '○ Disabled'}
              </button>
            </div>
            <div className="tier-fields">
              <div>
                <label className="small-label">Price (USDT)</label>
                <input className="small-input" value={d.priceUSDT}
                  onChange={(e) => update(i, 'priceUSDT', e.target.value)} />
              </div>
              <div>
                <label className="small-label">Mining Days</label>
                <input className="small-input" value={d.miningDays}
                  onChange={(e) => update(i, 'miningDays', e.target.value.replace(/\D/g, ''))} />
              </div>
              <div>
                <label className="small-label">Daily (USDT)</label>
                <input className="small-input" value={d.dailyRewardUSDT}
                  onChange={(e) => update(i, 'dailyRewardUSDT', e.target.value)} />
              </div>
              <button
                className="btn btn-primary btn-sm"
                disabled={!changed || busy === i}
                onClick={() => save(i)}
              >
                {busy === i ? <Spinner /> : 'Save'}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
